import { ViewShell } from "@shared/components/menus/views/ViewShell"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { cx } from "@shared/components/untitledui/util/cx"
import type { NoteWords } from "@shared/unified-tiptap/extensions/date-annotation-note/date-annotation-note-extension"
import { NoteTiptap } from "@shared/unified-tiptap/NoteTiptap"
import { objectId } from "@shared/util/object-id"
import type { Editor } from "@tiptap/core"
import { useMemo, useRef, useState } from "react"
import { noteDrafts } from "./note-draft"
import styles from "./NoteEditorView.module.css"

/** The field's own words, in place of the prompts a note painted on a day wears. */
const PROMPTS = { title: "Type a Note", description: "Add a description" }

// One editor is open at a time — the menu shows one view — so these need no generated suffix.
const HINT_ID = "note-editor-hint"
const ERROR_ID = "note-editor-error"

/**
 * Writing the note that says why the class isn't meeting — the note itself, in the shape it will
 * take on the day, rather than a form standing in for it. Nothing is written until Create: the
 * caller turns the class off with these words.
 */
export function NoteEditorView({
  color,
  dateString,
  draftKey,
  onCancel,
  onSave,
}: {
  /** The course's color, so the note is written on the ground it will be painted on. */
  color: string
  dateString: string
  /** Names the draft this view resumes and writes to — see {@link noteDrafts}. */
  draftKey: string
  onCancel: () => void
  onSave: (note: NoteWords) => void
}) {
  // The editor's doc needs one; the annotation the caller builds gets its own.
  const annotationId = useMemo(() => objectId(), [])
  const draft = useMemo(() => noteDrafts.read(draftKey), [draftKey])
  const editorRef = useRef<Editor | null>(null)
  const [words, setWords] = useState(draft)
  const [pressedWithoutTitle, setPressedWithoutTitle] = useState(false)

  // The editor reads a field with nothing in it as null, so this needs no trimming of its own.
  const hasTitle = words.title !== null
  const titleMissing = pressedWithoutTitle && !hasTitle

  const create = () => {
    if (!hasTitle) {
      setPressedWithoutTitle(true)
      // Where the words have to go — saying what is wrong and leaving the user to find it is
      // half an answer.
      editorRef.current?.commands.focus("start")
      return
    }
    noteDrafts.clear(draftKey)
    onSave(words)
  }

  return (
    <ViewShell
      actions={
        <>
          <Button color="secondary" size="md" className="flex-1" onPress={onCancel}>
            Cancel
          </Button>
          {/* Pressable while the title is blank, not disabled: a disabled button takes no
              press and no focus, so it could not say what it is waiting for. */}
          <Button
            data-test="view confirm"
            color="primary"
            size="md"
            className={cx("flex-1", !hasTitle && "opacity-50")}
            aria-describedby={titleMissing ? ERROR_ID : undefined}
            onPress={create}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="px-5 pt-4">
        <div
          data-test="note editor"
          className={cx(
            "rounded-lg bg-primary px-3 py-2 shadow-xs ring-1 ring-primary ring-inset",
            styles.field,
          )}
        >
          <NoteTiptap
            permissions={{ canEdit: true, canComment: false }}
            annotationId={annotationId}
            color={color}
            dateString={dateString}
            initialTitle={draft.title ?? ""}
            initialDescription={draft.description ?? ""}
            autofocus="start"
            ariaLabel="Note"
            ariaDescribedBy={HINT_ID}
            onSubmitShortcut={create}
            onUpdate={(typed) => {
              setWords(typed)
              // Every keystroke, so the words survive an Escape or a press outside the menu.
              noteDrafts.save(draftKey, typed)
            }}
            // Nothing to add to or delete: this note isn't on the day yet.
            hideButtons
            prompts={PROMPTS}
            // Create is what writes this note; losing focus commits nothing.
            onCommit={() => {}}
            editorRef={editorRef}
          />
        </div>
        <p className="sr-only" id={HINT_ID}>
          Type the note’s title. Press Enter to add a description.
        </p>
        {titleMissing && (
          <p
            data-test="note editor error"
            id={ERROR_ID}
            role="alert"
            className="m-0 px-1 pt-2 text-sm text-error-primary"
          >
            A note needs a title.
          </p>
        )}
      </div>
    </ViewShell>
  )
}
